import React, { useState, useEffect, useRef, useCallback } from "react"

import Select from "react-select"

import Flatpickr from "react-flatpickr"

import moment from "moment"

import { Button, Col, Form, Input, Label, Row } from "reactstrap"

import "flatpickr/dist/themes/material_blue.css"

import {
  expandMultiSelectValue,
} from "utils/providerDashboardFilters"

import {
  clearDescendantFilters,
  getDescendantFilterKeys,
  hasActiveFilterValue,
  pruneInvalidFilterSelections,
} from "utils/dashboardFilterCascade"

import getDashboardSelectProps from "utils/dashboardFilterStyles"

import "./ProviderDashboardFilterPanel.scss"

const OPTIONS_REFRESH_DEBOUNCE_MS = 300

const ProviderDashboardFilterPanel = ({
  definitions = [],
  options = {},
  initialFilters = {},
  loading = false,
  optionsLoading = false,
  onChange,
  onClearAll,
  onOptionsRefresh,
  activeContexts = null,
}) => {
  const [filters, setFilters] = useState(initialFilters)
  const [openMenuKey, setOpenMenuKey] = useState(null)
  const optionsRefreshRef = useRef(null)

  const visibleDefinitions = activeContexts
    ? definitions.filter(
        def =>
          def.contexts &&
          def.contexts.some(ctx => activeContexts.includes(ctx))
      )
    : definitions

  const effectiveDefinitions = visibleDefinitions.filter(def => {
    if (def.depends_on && def.depends_on.length > 0) {
      return def.depends_on.some(parentKey => hasActiveFilterValue(filters[parentKey]))
    }
    return true
  })

  useEffect(() => {
    setFilters(initialFilters)
  }, [initialFilters])

  useEffect(() => {
    return () => clearTimeout(optionsRefreshRef.current)
  }, [])

  const scheduleOptionsRefresh = useCallback(
    nextFilters => {
      if (!onOptionsRefresh) {
        return
      }

      clearTimeout(optionsRefreshRef.current)
      optionsRefreshRef.current = setTimeout(() => {
        onOptionsRefresh(nextFilters)
      }, OPTIONS_REFRESH_DEBOUNCE_MS)
    },
    [onOptionsRefresh]
  )

  useEffect(() => {
    setFilters(current => {
      const prunedFilters = pruneInvalidFilterSelections(
        current,
        definitions,
        options
      )
      if (prunedFilters !== current) {
        onChange(prunedFilters)
        return prunedFilters
      }
      return current
    })
  }, [options, definitions, onChange])

  const applyFilterChange = useCallback(
    (nextFilters, changedKey = null) => {
      setFilters(nextFilters)
      onChange(nextFilters)

      if (changedKey && getDescendantFilterKeys(changedKey, definitions).size > 0) {
        scheduleOptionsRefresh(nextFilters)
      }
    },
    [definitions, onChange, scheduleOptionsRefresh]
  )

  const updateFilter = (key, value) => {
    const nextFilters = clearDescendantFilters(
      { ...filters, [key]: value },
      key,
      definitions
    )
    applyFilterChange(nextFilters, key)
  }

  const handleMultiSelectChange = (key, selected, optionList = []) => {
    updateFilter(key, expandMultiSelectValue(selected, optionList))
  }

  const handleTextListChange = (key, value) => {
    const values = value
      .split(",")
      .map(item => item.trim())
      .filter(Boolean)
    updateFilter(key, values)
  }

  const clearField = key => {
    const nextFilters = clearDescendantFilters({ ...filters }, key, definitions)
    delete nextFilters[key]
    applyFilterChange(nextFilters, key)
  }

  const renderField = definition => {
    const optionList = options[definition.options_key] || []
    const currentValue = filters[definition.key]
    const setMenuOpen = open => {
      setOpenMenuKey(open ? definition.key : null)
    }

    if (definition.type === "multi_select") {
      return (
        <Select
          isMulti
          placeholder="All"
          options={optionList}
          value={currentValue || null}
          onChange={selected =>
            handleMultiSelectChange(definition.key, selected, optionList)
          }
          isLoading={optionsLoading}
          {...getDashboardSelectProps(setMenuOpen)}
        />
      )
    }

    if (definition.type === "multi_text") {
      const textValue = Array.isArray(currentValue)
        ? currentValue.join(", ")
        : currentValue || ""

      return (
        <Input
          type="text"
          value={textValue}
          placeholder="Comma-separated values"
          onChange={event =>
            handleTextListChange(definition.key, event.target.value)
          }
        />
      )
    }

    if (definition.type === "date") {
      return (
        <Flatpickr
          className="form-control d-block"
          placeholder="YYYY-MM-DD"
          value={currentValue || ""}
          options={{
            altInput: true,
            altFormat: "Y-m-d",
            dateFormat: "Y-m-d",
          }}
          onChange={value => {
            updateFilter(
              definition.key,
              value[0] ? moment(value[0]).format("YYYY-MM-DD") : null
            )
          }}
        />
      )
    }

    return (
      <Input
        type="text"
        value={currentValue || ""}
        onChange={event => updateFilter(definition.key, event.target.value)}
      />
    )
  }

  const groupedDefinitions = effectiveDefinitions.reduce((groups, definition) => {
    const group = definition.group || "general"
    if (!groups[group]) {
      groups[group] = []
    }
    groups[group].push(definition)
    return groups
  }, {})

  if (!visibleDefinitions.length) {
    return (
      <div className="alert alert-warning mb-0">
        No filter fields are available for this view.
      </div>
    )
  }

  return (
    <Form
      className="provider-dashboard-filters"
      onSubmit={event => {
        event.preventDefault()
        onChange(filters)
      }}
    >
      {Object.entries(groupedDefinitions).map(([group, fields]) => (
        <div key={group} className="mb-4">
          <h6 className="text-uppercase text-muted mb-3">
            {group.replace("_", " ")} Filters
          </h6>
          <Row>
            {fields.map(definition => (
              <Col md="4" className="filter-field-col" key={definition.key}>
                <div
                  className={`filter-field${
                    openMenuKey === definition.key ? " is-menu-open" : ""
                  }`}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <Label className="form-label mb-1">{definition.label}</Label>
                    {filters[definition.key] ? (
                      <Button
                        type="button"
                        color="link"
                        className="p-0 text-danger"
                        onClick={() => clearField(definition.key)}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                  {renderField(definition)}
                </div>
              </Col>
            ))}
          </Row>
        </div>
      ))}

      <div className="d-flex flex-wrap gap-2">
        <Button color="primary" type="submit" disabled={loading || optionsLoading}>
          {loading || optionsLoading ? "Updating..." : "Apply Filters"}
        </Button>
        <Button
          color="danger"
          type="button"
          outline
          onClick={() => {
            setFilters({})
            onClearAll()
          }}
        >
          Reset All
        </Button>
      </div>
    </Form>
  )
}

export default ProviderDashboardFilterPanel
